import { ArrowRightIcon, BriefcaseIcon, CheckCheckIcon, CheckIcon, ChevronDownIcon, CornerDownLeftIcon, HardHatIcon, HistoryIcon, Loader2Icon, SendIcon, UsersIcon, ZapIcon } from 'lucide-react';
import { Caret, ENTER } from '@/components/brand/PhoneConversation';
import { cn } from '@/lib/utils';

/**
 * The sender's side of the sign-in demo: the Crewline app as a mobile app, rendered inside a
 * `PhoneFrame`. It reuses the app's own tokens (square corners, `bg-primary` button, hairlines) so
 * it reads as the real product shrunk down, and every state is driven by the demo timeline in
 * SignInDemo: the notice is typed (or drafted by a weather rule), sent, delivered with the language
 * split, then a keyword comes in and the auto-reply goes out. Purely decorative.
 */

export interface AppJob {
  name: string;
  number: string;
  contacts: number;
}

export interface AppInbound {
  keyword: string;
  from: string;
  at: string;
  /** The auto-reply has gone out. */
  replied: boolean;
}

export function AppScreen({
  appName,
  job,
  draft,
  typing,
  rendered,
  rule,
  phase,
  sentAt,
  progress,
  delivered,
  skipped,
  languages,
  inbound,
  dim = false,
}: {
  appName: string;
  job: AppJob;
  /** Text in the message box right now. */
  draft: string;
  /** Show the caret (someone is typing). */
  typing: boolean;
  /** Exactly what recipients will see, for the character and segment count. */
  rendered: string;
  /** When set, the notice was drafted by this automation rule instead of a person. */
  rule?: string | null;
  phase: 'compose' | 'sending' | 'sent';
  sentAt: string;
  /** 0..1 progress of the delivery animation. */
  progress: number;
  delivered: number;
  skipped: number;
  languages: ReadonlyArray<readonly [string, number]>;
  inbound?: AppInbound | null;
  /** Fade the working area out (between demo scenarios). */
  dim?: boolean;
}) {
  const chars = draft ? rendered.length : 0;
  const segments = chars === 0 ? 0 : chars <= 160 ? 1 : Math.ceil(chars / 153);
  const deliveredNow = Math.round(progress * delivered);
  const settled = progress >= 1;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* App bar */}
      <div className="flex shrink-0 items-center justify-between px-4 pt-1 pb-2">
        <span className="font-display text-[15px] leading-none tracking-tight">{appName}</span>
        <span className="bg-foreground/10 flex size-6 items-center justify-center rounded-full text-[8px] font-semibold">DR</span>
      </div>

      {/* Job switcher */}
      <div className="mx-3 flex shrink-0 items-center gap-2 border px-2.5 py-1.5">
        <span className="bg-primary text-primary-foreground flex size-7 shrink-0 items-center justify-center">
          <HardHatIcon className="size-3.5" strokeWidth={2} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[11px] leading-tight font-semibold">{job.name}</span>
          <span className="text-muted-foreground block text-[9px] leading-tight tabular-nums">
            {job.number} · {job.contacts} contacts
          </span>
        </span>
        <ChevronDownIcon className="text-muted-foreground size-3.5 shrink-0" />
      </div>

      {/* Working area */}
      <div className={cn('flex min-h-0 flex-1 flex-col overflow-hidden px-3 pt-2.5 pb-1 transition-opacity duration-500', dim && 'opacity-0')}>
        {rule ? (
          <div className={cn('bg-warning/15 mb-2 flex items-start gap-1.5 border px-2 py-1.5 text-[9px] leading-snug', ENTER)}>
            <ZapIcon className="text-warning mt-px size-3 shrink-0" strokeWidth={2.25} aria-hidden />
            <span>
              <span className="font-medium">Weather rule matched</span> · {rule}. Sent automatically; quiet hours overridden for safety.
            </span>
          </div>
        ) : null}

        <p className="text-[10px] font-medium">Message</p>
        <div className="bg-background mt-1 min-h-[64px] border px-2.5 py-1.5 text-[10.5px] leading-[1.45]">
          {draft ? (
            <>
              {draft}
              {typing ? <Caret /> : null}
            </>
          ) : (
            <span className="text-muted-foreground">Write it once. Everyone gets it in their language.</span>
          )}
        </div>
        <p className="text-muted-foreground mt-1 text-[8.5px] tabular-nums">
          {chars} chars · {segments} {segments === 1 ? 'segment' : 'segments'} · label + opt-out footer added
        </p>

        <div className="mt-2 flex items-center justify-between text-[9.5px]">
          <span className="text-muted-foreground">To</span>
          <span className="font-medium">Everyone on the job · {job.contacts}</span>
        </div>

        {phase === 'sent' ? (
          <p className={cn('text-muted-foreground mt-2 flex h-7 items-center gap-1.5 text-[9.5px]', ENTER)}>
            <CheckIcon className="text-success size-3" strokeWidth={2.5} aria-hidden />
            {rule ? 'Sent automatically' : `Sent to ${job.contacts}`} · {sentAt}
          </p>
        ) : (
          <div className={cn('bg-primary text-primary-foreground mt-2 flex h-7 items-center justify-center gap-1.5 text-[10px] font-medium transition-transform duration-200', phase === 'sending' && 'scale-[0.97]')}>
            {phase === 'sending' ? (
              <>
                <Loader2Icon className="size-3 animate-spin" aria-hidden /> Sending…
              </>
            ) : rule ? (
              <>
                <ZapIcon className="size-3" aria-hidden /> Auto-send is on
              </>
            ) : (
              <>
                Send to {job.contacts}
                <ArrowRightIcon className="size-3" aria-hidden />
              </>
            )}
          </div>
        )}

        {phase === 'sent' ? (
          <div className={cn('mt-2 border-t pt-2', ENTER)}>
            <div className="flex items-center justify-between text-[9.5px]">
              <span className="flex items-center gap-1.5 font-medium">
                <CheckCheckIcon className="text-primary size-3.5" aria-hidden />
                Delivered
              </span>
              <span className="text-muted-foreground tabular-nums">
                {deliveredNow} of {job.contacts}
              </span>
            </div>
            <div className="bg-foreground/10 mt-1.5 h-1 w-full overflow-hidden">
              <div className="bg-primary h-full" style={{ width: `${(deliveredNow / job.contacts) * 100}%` }} />
            </div>
            <div className={cn('mt-1.5 flex flex-wrap gap-1 text-[8.5px] transition-opacity duration-500', settled ? 'opacity-100' : 'opacity-0')}>
              {languages.map(([code, n]) => (
                <span key={code} className="bg-muted text-muted-foreground px-1.5 py-0.5 tabular-nums">
                  {code} {n}
                </span>
              ))}
              {skipped > 0 ? <span className="bg-muted text-muted-foreground px-1.5 py-0.5 tabular-nums">{skipped} opted out · skipped</span> : null}
            </div>
          </div>
        ) : null}

        {inbound ? (
          <div className={cn('mt-2 border-t pt-2 text-[9.5px]', ENTER)}>
            <div className="flex items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-1.5">
                <CornerDownLeftIcon className="text-muted-foreground size-3 shrink-0" aria-hidden />
                <span className="bg-primary text-primary-foreground shrink-0 px-1.5 py-px text-[8.5px] font-medium tracking-wide">{inbound.keyword}</span>
                <span className="text-muted-foreground truncate tabular-nums">{inbound.from}</span>
              </span>
              <span className="text-muted-foreground shrink-0 tabular-nums">{inbound.at}</span>
            </div>
            <p className={cn('text-muted-foreground mt-1 flex items-center gap-1.5 transition-opacity duration-300', inbound.replied ? 'opacity-100' : 'opacity-0')}>
              <CheckIcon className="text-success size-3" strokeWidth={2.5} aria-hidden />
              Auto-reply sent in English
            </p>
          </div>
        ) : null}
      </div>

      {/* Bottom tab bar */}
      <div className="border-foreground/10 flex shrink-0 items-start justify-around border-t px-2 pt-1.5 pb-0.5 text-[7.5px] font-medium">
        <Tab icon={BriefcaseIcon} label="Jobs" />
        <Tab icon={SendIcon} label="Compose" active />
        <Tab icon={HistoryIcon} label="History" />
        <Tab icon={UsersIcon} label="Team" />
      </div>
    </div>
  );
}

function Tab({ icon: Icon, label, active = false }: { icon: typeof SendIcon; label: string; active?: boolean }) {
  return (
    <span className={cn('flex flex-col items-center gap-0.5', active ? 'text-primary' : 'text-muted-foreground')}>
      <Icon className="size-3.5" strokeWidth={active ? 2.25 : 1.75} />
      {label}
    </span>
  );
}
